import * as React from 'react';

import { BaseModalProps } from 'shared/newEntities/components/modals';
import {
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import Typography from 'shared/newComponents/Typography';
import { TranslationString } from 'shared/entities/localization';
import { ReferenceProps } from 'shared/newEntities/components/Reference';

import BaseModal from '../BaseModal';

import ModalHeader from './ModalHeader';

import './Modal.modules.scss';

type Props = React.PropsWithChildren<
  BaseModalProps & {
    title?: TranslationString | React.ReactNode;
    /** Вторая строка шапки, под заголовком и на всю ширину */
    subTitle?: TranslationString | string;
    /** Доступ к заголовку для программного переноса фокуса */
    titleRef?: React.RefObject<HTMLHeadingElement>;
    headerExtraComponent?: React.ReactNode;
    headerRightComponent?: React.ReactNode;
    headerLeftComponent?: React.ReactNode;
    className?: string;
    contentClassName?: string;
    titleFontSize?: TypographySize.l | TypographySize.m;
    reference?: ReferenceProps;
    style?: React.CSSProperties;
    withHeader?: boolean;
  }
>;

const Modal = React.forwardRef(
  (
    {
      opened,
      onClose,
      children,
      className,
      title,
      subTitle,
      titleRef,
      headerExtraComponent,
      headerRightComponent,
      headerLeftComponent,
      contentClassName,
      titleFontSize,
      reference,
      style,
      withHeader = true,
      dismissible = true,
      closeOnEscape = dismissible,
      ariaLabel,
      ariaLabelledBy,
      initialFocusRef,
      fullScreenMobile
    }: Props,
    ref: React.MutableRefObject<HTMLDivElement | null>
  ) => {
    const generatedTitleId = React.useId();
    const generatedSubTitleId = React.useId();
    const actualAriaLabelledBy =
      ariaLabelledBy || (title ? generatedTitleId : undefined);

    return (
      <BaseModal
        opened={opened}
        onClose={onClose}
        styleName="modal__content"
        className={className}
        style={style}
        ref={ref}
        dismissible={dismissible}
        closeOnEscape={closeOnEscape}
        ariaLabel={ariaLabel}
        ariaLabelledBy={actualAriaLabelledBy}
        initialFocusRef={initialFocusRef}
        fullScreenMobile={fullScreenMobile}
      >
        {withHeader && (
          <ModalHeader
            titleId={generatedTitleId}
            subTitleId={generatedSubTitleId}
            title={title}
            subTitle={subTitle}
            titleRef={titleRef}
            titleFontSize={titleFontSize}
            headerExtraComponent={headerExtraComponent}
            headerRightComponent={headerRightComponent}
            headerLeftComponent={headerLeftComponent}
            reference={reference}
            showCloseButton={dismissible}
            onClose={onClose}
          />
        )}
        <Typography
          fontType={TypographyType.text}
          fontSize={TypographySize.m}
          fontWeight={TypographyWeight.normal}
          className={contentClassName}
          styleName="modal__content-main"
        >
          {children}
        </Typography>
      </BaseModal>
    );
  }
);

export default Modal;
